/**
 * Create and edit one shift, on a phone.
 *
 * Same fields and same save contract as the desktop drawer; the difference is
 * that everything here is built for a thumb. People are a row of faces rather
 * than a dropdown, with the ones who have the fewest hours this week first and
 * anyone who has marked the day off greyed out with their reason. Times move in
 * quarter hours with big plus and minus buttons rather than a text field, and
 * the whole thing is a bottom sheet the other hand can dismiss.
 *
 * An availability clash is not refused. Picking a greyed person shows the
 * clash in plain words and the primary button becomes "Roster them anyway",
 * which sends `force: true`, exactly as the drawer does. That keeps the phone
 * and the laptop honest with each other about what a boss is allowed to do.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { CalendarOff, Lock, Minus, Plus, Trash2, Unlock } from 'lucide-react';
import type { Area, Shift, ShiftTemplate, StaffMember } from '../lib/api.ts';
import { DAY_SHORT, dayIndex, fmtDateMedium, fmtDuration, fmtMoney, fmtTime12, shiftMinutes, type Hhmm } from '../lib/time.ts';
import { Sheet } from '../components/Overlay.tsx';
import { personColours, Spinner } from '../components/Bits.tsx';
import type { ShiftForm } from './ShiftDrawer.tsx';
import { bucketForDate, type Rates, type UnavailableIndex } from './rosterMath.ts';

const toMin = (t: Hhmm) => {
  const [h, m] = t.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
const toHhmm = (m: number): Hhmm =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

const BREAKS = [0, 15, 30, 45, 60];

export function ShiftSheet({
  open,
  form,
  staff,
  areas,
  templates,
  shifts,
  rates,
  unavailable,
  coverPanel,
  saving,
  deleting,
  isOwner,
  onClose,
  onSave,
  onDelete,
}: {
  open: boolean;
  form: ShiftForm | null;
  staff: StaffMember[];
  areas: Area[];
  templates: ShiftTemplate[];
  /** The whole cached week, for hours per person and same-day clashes. */
  shifts: Shift[];
  rates: Record<string, Rates>;
  unavailable: UnavailableIndex;
  coverPanel?: ReactNode;
  saving: boolean;
  deleting: boolean;
  isOwner: boolean;
  onClose: () => void;
  onSave: (values: ShiftForm, force: boolean) => void;
  onDelete: (id: string) => void;
}) {
  const [values, setValues] = useState<ShiftForm | null>(form);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [templateId, setTemplateId] = useState<string | null>(null);

  // Keep the last form while the sheet animates closed, so it does not blank
  // mid-slide. A sheet that has never had a form has nothing to draw.
  useEffect(() => {
    if (!form) return;
    setValues(form);
    setConfirmDelete(false);
    setTemplateId(null);
  }, [form]);

  const colours = useMemo(() => personColours(staff), [staff]);
  const activeAreas = useMemo(() => areas.filter((a) => a.active !== false).sort((a, b) => a.position - b.position), [areas]);
  const activeTemplates = useMemo(
    () => templates.filter((t) => t.active !== false).sort((a, b) => a.position - b.position),
    [templates],
  );

  const hoursThisWeek = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of shifts) {
      if (!s.userId) continue;
      map.set(s.userId, (map.get(s.userId) ?? 0) + shiftMinutes(s.startTime, s.endTime, s.breakMinutes));
    }
    return map;
  }, [shifts]);

  const people = useMemo(() => {
    if (!values) return [];
    const from = toMin(values.startTime);
    const to = toMin(values.endTime);
    return staff
      .filter((s) => s.active !== false)
      .map((p) => {
        const off = unavailable.has(p.userId, values.date);
        const busy = shifts.some(
          (s) =>
            s.userId === p.userId &&
            s.date === values.date &&
            s.id !== values.id &&
            toMin(s.startTime.slice(0, 5)) < to &&
            toMin(s.endTime.slice(0, 5)) > from,
        );
        return { p, off, busy, note: unavailable.noteFor(p.userId, values.date), minutes: hoursThisWeek.get(p.userId) ?? 0 };
      })
      .sort(
        (a, b) =>
          Number(b.p.userId === values.userId) - Number(a.p.userId === values.userId) ||
          Number(a.off) - Number(b.off) ||
          Number(a.busy) - Number(b.busy) ||
          a.minutes - b.minutes,
      );
  }, [staff, shifts, unavailable, values, hoursThisWeek]);

  if (!values) return null;

  const isNew = values.id === null;
  const clash = !!values.userId && unavailable.has(values.userId, values.date);
  const clashName = clash ? (staff.find((s) => s.userId === values.userId)?.name ?? 'They') : '';
  const clashNote = clash ? unavailable.noteFor(values.userId, values.date) : null;
  const minutes = shiftMinutes(values.startTime, values.endTime, values.breakMinutes);
  const rate = values.userId ? (rates[values.userId]?.[bucketForDate(values.date)] ?? 0) : 0;
  const cost = values.userId ? (minutes / 60) * rate : 0;
  const person = values.userId ? staff.find((s) => s.userId === values.userId) : null;
  const locked = values.locked;

  const set = (patch: Partial<ShiftForm>) => setValues((v) => (v ? { ...v, ...patch } : v));

  const nudge = (key: 'startTime' | 'endTime', dir: 1 | -1) => {
    let s = toMin(values.startTime);
    let e = toMin(values.endTime);
    if (key === 'startTime') s = Math.max(0, Math.min(23 * 60 + 45, s + dir * 15));
    else e = Math.max(0, Math.min(23 * 60 + 45, e + dir * 15));
    // Keep at least half an hour on the shift, and never let it go backwards.
    if (e - s < 30) return;
    set({ startTime: toHhmm(s), endTime: toHhmm(e) });
    setTemplateId(null);
  };

  const pickTemplate = (t: ShiftTemplate) => {
    setTemplateId(t.id);
    set({ startTime: t.startTime.slice(0, 5), endTime: t.endTime.slice(0, 5) });
  };

  const title = isNew ? 'New shift' : locked ? 'Locked shift' : 'Edit shift';
  const subtitle = `${DAY_SHORT[dayIndex(values.date)]} ${fmtDateMedium(values.date)}`;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      footer={
        <div className="flex w-full items-center gap-2">
          {!isNew && !locked ? (
            confirmDelete ? (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => values.id && onDelete(values.id)}
                disabled={deleting}
              >
                {deleting ? <Spinner size={14} /> : <Trash2 size={15} aria-hidden="true" />}
                Delete it
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-icon"
                onClick={() => setConfirmDelete(true)}
                title="Delete shift|You get 12 seconds to undo."
                aria-label="Delete shift"
              >
                <Trash2 size={16} aria-hidden="true" />
              </button>
            )
          ) : null}
          <button
            type="button"
            className={`btn flex-1 ${clash ? 'btn-danger' : 'btn-primary'}`}
            onClick={() => onSave(values, clash)}
            disabled={saving || locked}
          >
            {saving ? <Spinner size={14} /> : null}
            {clash ? `Roster ${clashName.split(' ')[0]} anyway` : isNew ? (values.userId ? 'Add to roster' : 'Add as unfilled') : 'Save'}
          </button>
        </div>
      }
    >
      <div className="ss">
        {locked ? (
          <div className="ss-note">
            <Lock size={16} aria-hidden="true" />
            <div>
              <b>Somebody has clocked against this shift, so it is locked.</b>
              {isOwner ? (
                <button type="button" className="btn btn-sm mt-2" onClick={() => set({ locked: false })}>
                  <Unlock size={14} aria-hidden="true" />
                  Unlock and edit
                </button>
              ) : (
                <span>Only an owner can unlock it.</span>
              )}
            </div>
          </div>
        ) : null}

        {isNew && activeTemplates.length > 0 ? (
          <>
            <div className="ss-lbl">Start from</div>
            <div className="ss-tpl" style={{ gridTemplateColumns: `repeat(${Math.min(3, activeTemplates.length)}, minmax(0, 1fr))` }}>
              {activeTemplates.slice(0, 6).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={templateId === t.id ? 'is-on' : ''}
                  onClick={() => pickTemplate(t)}
                  aria-pressed={templateId === t.id}
                >
                  <b>{t.name}</b>
                  <span className="tabular">
                    {fmtTime12(t.startTime)} to {fmtTime12(t.endTime)}
                  </span>
                </button>
              ))}
            </div>
          </>
        ) : null}

        <div className="ss-lbl">
          Who <small>fewest hours this week first</small>
        </div>
        <div className="ss-rail" role="radiogroup" aria-label="Who works it">
          {people.map(({ p, off, busy, note, minutes: m }) => {
            const on = values.userId === p.userId;
            return (
              <button
                key={p.userId}
                type="button"
                role="radio"
                aria-checked={on}
                className={`ss-pp ${on ? 'is-on' : ''} ${off ? 'is-off' : ''}`}
                style={{ ['--ss-solid' as string]: colours.get(p.userId) ?? 'var(--sp-tpl-slate)' }}
                onClick={() => set({ userId: on ? null : p.userId })}
                disabled={locked}
                title={off ? `${p.name} is unavailable|${note ?? 'Marked this day off.'}` : busy ? `${p.name} is already on this day|Overlaps another shift.` : undefined}
              >
                <span className="ss-av" aria-hidden="true">
                  {p.name[0]}
                  {busy && !off ? <i className="ss-busy">!</i> : null}
                </span>
                <span className="ss-nm">{p.name.split(' ')[0]}</span>
                {off ? (
                  <span className="ss-why">{note ? note.slice(0, 14) : 'Day off'}</span>
                ) : (
                  <span className="ss-h tabular">{m ? fmtDuration(m) : '0h'}</span>
                )}
              </button>
            );
          })}
        </div>

        {clash ? (
          <div className="ss-note is-warn">
            <CalendarOff size={16} aria-hidden="true" />
            <div>
              <b>{clashName} marked this day unavailable.</b>
              <span>{clashNote ? `They said: "${clashNote}".` : 'No reason given.'} You can roster them anyway; they are told either way.</span>
            </div>
          </div>
        ) : null}

        <div className="ss-lbl">Times</div>
        <div className="ss-times">
          {(['startTime', 'endTime'] as const).map((k) => (
            <div className="ss-tp" key={k}>
              <span className="ss-k">{k === 'startTime' ? 'Start' : 'Finish'}</span>
              <span className="ss-v tabular">{fmtTime12(values[k])}</span>
              <button type="button" onClick={() => nudge(k, -1)} aria-label={`${k === 'startTime' ? 'Earlier start' : 'Earlier finish'}, 15 minutes`} disabled={locked}>
                <Minus size={16} aria-hidden="true" />
              </button>
              <button type="button" onClick={() => nudge(k, 1)} aria-label={`${k === 'startTime' ? 'Later start' : 'Later finish'}, 15 minutes`} disabled={locked}>
                <Plus size={16} aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>

        <div className="ss-lbl">Break</div>
        <div className="ss-brk">
          {BREAKS.map((b) => (
            <button
              key={b}
              type="button"
              className={values.breakMinutes === b ? 'is-on' : ''}
              aria-pressed={values.breakMinutes === b}
              onClick={() => set({ breakMinutes: b })}
              disabled={locked}
            >
              {b ? `${b}m` : 'None'}
            </button>
          ))}
        </div>

        {activeAreas.length > 0 ? (
          <>
            <div className="ss-lbl">Area</div>
            <div className="ss-seg" style={{ gridTemplateColumns: `repeat(${Math.min(3, activeAreas.length + 0)}, minmax(0, 1fr))` }}>
              {activeAreas.slice(0, 3).map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className={values.areaId === a.id ? 'is-on' : ''}
                  aria-pressed={values.areaId === a.id}
                  onClick={() => set({ areaId: values.areaId === a.id ? null : a.id })}
                  disabled={locked}
                >
                  {a.name}
                </button>
              ))}
            </div>
          </>
        ) : null}

        {coverPanel}

        <label className="ss-lbl" htmlFor="ss-notes">
          Note <small>optional, the person sees it</small>
        </label>
        <input
          id="ss-notes"
          className="field"
          value={values.notes}
          onChange={(e) => set({ notes: e.target.value })}
          placeholder="Bring the keys, cake delivery at 10"
          disabled={locked}
          maxLength={300}
        />

        <div className="ss-sum">
          <span>
            {person
              ? `${person.name.split(' ')[0]}, ${fmtDuration(minutes)} paid at ${fmtMoney(rate)}/h`
              : `${fmtDuration(minutes)}, pick somebody to see the cost`}
          </span>
          <b className="tabular">{person ? fmtMoney(cost, { cents: false }) : ''}</b>
        </div>
      </div>
    </Sheet>
  );
}
